import gql from 'graphql-tag';
import I18n from 'i18n-js';
import React from 'react';

import CheckSvg from '@reverbdotcom/commons/design-system/icons/core/check.svg?component';
import { RCTextWithIcon } from '@reverbdotcom/commons/src/rc/RCTextWithIcon';

import { SellFormKeyValueList } from './form/components/SellFormKeyValueList';
import { CARRIER_CALC_RATE_TYPE } from './form/components/shipping/ShippingForm';
import { core_apimessages_ShippingRates, ShipmentPackage } from '../../schemas/rql';

export const MyShippingProfilePricesFragment = gql`
  fragment MyShippingProfilePrices on rql_Me {
    uuid
    shop {
      _id
      shippingProfiles {
        id
        name
        localPickup
        shippingRates {
          regionCode
          regionName
          rate {
            display
          }
          incrementalRate {
            display
          }
          expeditedRate {
            display
          }
        }
      }
    }
  }
`;

enum PriceField {
  STANDARD = 'rate',
  COMBINED = 'incrementalRate',
  EXPEDITED = 'expeditedRate',
}

interface IProps {
  offersShipping: boolean;
  offersLocalPickup: boolean;
  shippingRates: core_apimessages_ShippingRates[];
  shipmentPackage?: ShipmentPackage;
}

function ShippingRateSummary({ shippingRate }: { shippingRate: core_apimessages_ShippingRates }) {
  const standardShippingOnly = shippingRate.rate?.display && !shippingRate.incrementalRate?.display && !shippingRate.expeditedRate?.display;

  return (
    <>
      {standardShippingOnly && <div>{shippingRate.rate.display}</div>}

      {!standardShippingOnly && (
        <>
          {[PriceField.STANDARD, PriceField.COMBINED, PriceField.EXPEDITED].map((type) => (
            shippingRate[type]?.display && (
              <div key={type} className="mb-half">
                <span className="mr-half">
                  <strong>
                    {I18n.t(`discovery.sellForm.shippingSection.rateType.${type}`)}
                  </strong>
                </span>
                <span>
                  {shippingRate[type]?.display}
                </span>
              </div>
            )
          ))}
        </>
      )}
    </>
  );
}

function PackageSummary({ shipmentPackage }: { shipmentPackage: ShipmentPackage }) {
  const canSummarizePackage = ['length', 'width', 'height', 'weight'].every((dim) => shipmentPackage[dim]?.value && shipmentPackage[dim]?.unit);

  return (
    <>
      <div className="mb-half size-110">
        {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedSummary')}
      </div>

      {canSummarizePackage && (
        <>
          <div className="mb-half">
            <span className="mr-half">
              <strong>
                {I18n.t('discovery.sellForm.shippingSection.packageLengthSummary')}
              </strong>
            </span>
            <span>
              {['length', 'width', 'height'].map((dim) => `${shipmentPackage[dim].value} ${shipmentPackage[dim].unit}`).join(' X ')}
            </span>
          </div>
          <div>
            <span className="mr-half">
              <strong>
                {I18n.t('discovery.sellForm.shippingSection.packageWeightSumamry')}
              </strong>
            </span>
            <span>
              {`${shipmentPackage.weight.value} ${shipmentPackage.weight.unit}`}
            </span>
          </div>
        </>
      )}
    </>
  );
}

export default function ShippingSummary({ offersShipping, offersLocalPickup, shippingRates = [], shipmentPackage }: IProps) {
  const displayShippingMethods = [
    offersShipping && I18n.t('discovery.sellForm.shippingSection.deliveryOptions.shipping'),
    offersLocalPickup && I18n.t('discovery.sellForm.shippingSection.deliveryOptions.localPickup'),
  ].filter(v => v);

  return (
    <div>
      <div data-delivery-options>
        <strong>
          {I18n.t('discovery.sellForm.shippingSection.deliveryOptionsHeader')}
        </strong>
        {displayShippingMethods.map((method) => (
          <div key={method} className="mt-1">
            <RCTextWithIcon svgComponent={CheckSvg} placement="left">
              {method}
            </RCTextWithIcon>
          </div>
        ))}
      </div>

      {offersShipping && shippingRates.length > 0 && (
        <div className="bg-white" data-shipping-rates-list>
          <div className="mt-3">
            <strong>
              {I18n.t('discovery.sellForm.shippingSection.costsPerRegion')}
            </strong>
          </div>
          <SellFormKeyValueList>
            {shippingRates.map((sr) => (
              <SellFormKeyValueList.Row label={I18n.t(`discovery.shippingRegions.${sr.regionCode}`)} key={sr.regionCode}>
                {sr.rateType === CARRIER_CALC_RATE_TYPE && <PackageSummary shipmentPackage={shipmentPackage} />}

                {sr.rateType !== CARRIER_CALC_RATE_TYPE && <ShippingRateSummary shippingRate={sr} />}
              </SellFormKeyValueList.Row>
            ))}
          </SellFormKeyValueList>
        </div>
      )}
    </div>
  );
}
